// AgencyCallModal — Agency Fulfilment Call Bot (STSMPD-1322).
//
// Lists the resolvable contacts for an allocation (agency head-office number + branch/account
// numbers, with a free-type fallback), shows the script the bot will read, and on submit POSTs a
// call request to the fulfilment-call-service. It then short-polls GET /call-requests/:id, mirrors
// the live `last_state` in the modal, raises notify() toasts on transitions, and switches to a
// terminal view once the call finishes (the durable record is the admin alert + admin note written
// server-side).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'

import { Modal } from 'syft-acp-core/components/Modal'
import config from 'syft-acp-core/config'
import { getModal$ } from 'syft-acp-core/store/modals/selectors'
import { hideModal } from 'syft-acp-core/store/modals/actions'
import { notify } from 'syft-acp-core/actions/notifications'
import { fetchAgencyProfile } from 'syft-acp-core/actions/agency-profile'
import { fetchAgencyAccounts } from 'syft-acp-core/actions/agency-accounts'
import { entityList$ } from 'syft-acp-core/reducers/generators/entities'

import { createCallRequest, getCallRequest, DEFAULT_SCRIPT_ID } from 'syft-acp-core/store/agency-call/api'
import { CallRequestView } from 'syft-acp-core/store/agency-call/types'
import { setAgencyCallState } from 'syft-acp-core/store/agency-call/actions'

import {
  AgencyCallModalOwnProps as OwnProps,
  AgencyCallModalProps as Props,
  AgencyCallModalStateProps as StateProps,
  AgencyCallModalDispatchProps as DispatchProps,
  AgencyCallModalItem,
  AgencyCallContactOption,
  AgencyAccount,
} from './AgencyCallModal.types'
import { buildScriptPreview } from './scriptPreview'
import {
  resolveContacts,
  resolveAgencyName,
  isLikelyValidPhone,
  isTerminalStatus,
  describeCallState,
  outcomeLabel,
  toastLevelForStatus,
} from './helpers'

const defaultModalName = 'agencyCallModal'

/** Poll interval + ceiling for the live call-state short-poll. */
const POLL_INTERVAL_MS = 3000
const MAX_POLLS = 200 // ~10 minutes of polling, then we stop and let the durable alert/note take over

const FREE_TYPE = '__free_type__'

export const storeConnector = connect<StateProps, DispatchProps, OwnProps, Record<string, any>>(
  (state, { name }) => {
    const item = getModal$(state.modals)(name || defaultModalName) as unknown as AgencyCallModalItem
    const agencyId = item?.data?.agency_id
    return {
      item,
      userData: state?.auth?.userData,
      agencyProfile: agencyId != null ? state?.agencyProfile?.entityDetail?.[agencyId] : undefined,
      agencyAccounts:
        agencyId != null ? (entityList$(state?.agencyAccounts, { agencyId }) as AgencyAccount[]) : [],
    }
  },
  (dispatch, { name }) => ({
    closeModal: () => dispatch(hideModal(name || defaultModalName)),
    fetchAgencyProfile: (options: { agencyId: number }) => dispatch(fetchAgencyProfile(options)),
    fetchAgencyAccounts: (payload: { agencyId: number }) => dispatch(fetchAgencyAccounts(payload)),
    notifyToast: (level: string, payload: Record<string, any>) => dispatch(notify(level, payload)),
    setCallState: (payload: Parameters<typeof setAgencyCallState>[0]) =>
      dispatch(setAgencyCallState(payload)),
  }),
)

const AgencyCallModal = ({
  item = {},
  userData,
  agencyProfile,
  agencyAccounts,
  closeModal,
  fetchAgencyProfile: fetchProfile,
  fetchAgencyAccounts: fetchAccounts,
  notifyToast,
  setCallState,
}: Props) => {
  const { isShown, data } = item

  const [selectedPhone, setSelectedPhone] = useState<string>('')
  const [freeTypePhone, setFreeTypePhone] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorText, setErrorText] = useState<string>('')
  const [callView, setCallView] = useState<CallRequestView | null>(null)

  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pollCount = useRef(0)
  const lastNotifiedState = useRef<string | null>(null)
  // Guards async continuations: an in-flight getCallRequest/createCallRequest can resolve AFTER the
  // modal is closed or unmounted. `stopPolling` only clears the *pending* timer — it can't cancel a
  // request already on the wire — so we also flip this to false on close/unmount and bail out of any
  // resolved .then/.catch before it can setState (on an unmounted tree) or re-arm the poll loop.
  const isActive = useRef(false)
  // Synchronous re-entry guard for the non-idempotent POST. `setIsSubmitting(true)` only disables the
  // button on the NEXT render, so a fast double-click / Enter could fire two createCallRequest calls
  // within the same tick; this ref is flipped synchronously so the second trigger is a no-op.
  const submitInFlight = useRef(false)

  const stopPolling = useCallback(() => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current)
      pollTimer.current = null
    }
  }, [])

  // Reset transient state + fetch contacts whenever the modal is (re)opened for an allocation.
  useEffect(() => {
    if (!isShown || !data?.agency_id) return undefined
    isActive.current = true
    submitInFlight.current = false
    setSelectedPhone('')
    setFreeTypePhone('')
    setErrorText('')
    setCallView(null)
    setIsSubmitting(false)
    pollCount.current = 0
    lastNotifiedState.current = null
    fetchProfile({ agencyId: data.agency_id })
    fetchAccounts({ agencyId: data.agency_id })
    return () => {
      // Closing/reopening (isShown or allocation changes) must stop the current poll loop AND
      // neutralise any request still in flight for the previous allocation.
      isActive.current = false
      stopPolling()
    }
  }, [isShown, data?.agency_id, fetchProfile, fetchAccounts, stopPolling])

  // Tear down any in-flight poll on unmount, and neutralise in-flight requests.
  useEffect(
    () => () => {
      isActive.current = false
      stopPolling()
    },
    [stopPolling],
  )

  const contacts = useMemo<AgencyCallContactOption[]>(
    () =>
      resolveContacts({
        preResolved: data?.contacts,
        agencyProfile,
        agencyAccounts,
        agencyName: data?.agency_name,
      }),
    [data?.contacts, data?.agency_name, agencyProfile, agencyAccounts],
  )

  // Default selection to the first resolvable contact once contacts arrive.
  useEffect(() => {
    if (!selectedPhone && contacts.length > 0) {
      setSelectedPhone(contacts[0].phone)
    }
  }, [contacts, selectedPhone])

  const scriptPreview = useMemo(() => (data ? buildScriptPreview(data) : ''), [data])

  const effectivePhone = selectedPhone === FREE_TYPE ? freeTypePhone.trim() : selectedPhone
  const effectiveLabel =
    selectedPhone === FREE_TYPE
      ? data?.agency_name || 'Manually entered number'
      : contacts.find(c => c.phone === selectedPhone)?.label

  const canSubmit = !isSubmitting && !callView && !!effectivePhone && isLikelyValidPhone(effectivePhone)

  const poll = useCallback(
    (callRequestId: string) => {
      pollCount.current += 1
      getCallRequest(callRequestId)
        .then(view => {
          // Bail if the modal was closed/unmounted while this request was in flight.
          if (!isActive.current) return
          setCallView(view)
          if (data?.allocation_id != null) {
            setCallState({
              allocationId: data.allocation_id,
              lastState: view.last_state,
              status: view.status,
              callRequestId: view.call_request_id,
            })
          }

          // Toast on each new last_state transition.
          const stateLabel = describeCallState(view)
          if (stateLabel && stateLabel !== lastNotifiedState.current) {
            lastNotifiedState.current = stateLabel
            notifyToast(isTerminalStatus(view.status) ? toastLevelForStatus(view.status) : 'info', {
              title: 'Agency fulfilment call',
              message: stateLabel,
              autoDismiss: isTerminalStatus(view.status) ? 0 : 5,
            })
          }

          if (isTerminalStatus(view.status)) {
            stopPolling()
            data?.refetch?.()
            return
          }
          if (pollCount.current >= MAX_POLLS) {
            stopPolling()
            return
          }
          pollTimer.current = setTimeout(() => poll(callRequestId), POLL_INTERVAL_MS)
        })
        .catch(() => {
          // Bail if the modal was closed/unmounted while this request was in flight.
          if (!isActive.current) return
          // Keep polling through transient errors until we hit the ceiling.
          if (pollCount.current < MAX_POLLS) {
            pollTimer.current = setTimeout(() => poll(callRequestId), POLL_INTERVAL_MS)
          }
        })
    },
    [data, stopPolling, notifyToast, setCallState],
  )

  const onConfirm = useCallback(() => {
    // Re-entry guard first: a fast double-click / Enter must not fire two POSTs (the disabled state
    // from setIsSubmitting only applies on the next render). callView means the call already started.
    if (submitInFlight.current || callView) return
    if (!data || !effectivePhone) return
    submitInFlight.current = true
    setErrorText('')
    setIsSubmitting(true)

    const requesterName = userData?.name || userData?.email || 'ACP user'
    createCallRequest({
      job_id: data.job_id,
      shift_id: data.shift_id,
      agency_id: data.agency_id,
      // Prefer the fetched profile's clean name; fall back to a normalized first
      // segment of the row's agency_name (it comes from allocation_name, a
      // comma-separated display string). Undefined -> the service resolves it.
      agency_name: resolveAgencyName(data.agency_name, agencyProfile),
      allocation_id: data.allocation_id,
      contact: { phone: effectivePhone, label: effectiveLabel },
      shift_context: {
        role_title: data.role_title,
        venue_name: data.venue_name,
        start_time: data.start_time,
        workers_required: data.workers_required,
        venue_timezone: data.venue_timezone,
      },
      script_id: DEFAULT_SCRIPT_ID,
      requested_by: {
        user_id: userData?.id != null ? String(userData.id) : undefined,
        email: userData?.email || '',
        name: requesterName,
      },
      slack_channel: config.fulfilmentCallSlackChannel || undefined,
    })
      .then(res => {
        submitInFlight.current = false
        // Bail if the modal was closed/unmounted before the create resolved.
        if (!isActive.current) return
        setIsSubmitting(false)
        setCallView({ call_request_id: res.call_request_id, status: res.status, last_state: 'Queued' })
        if (data.allocation_id != null) {
          setCallState({
            allocationId: data.allocation_id,
            lastState: 'Queued',
            status: res.status,
            callRequestId: res.call_request_id,
          })
        }
        notifyToast('success', {
          title: 'Agency fulfilment call',
          message: 'Call request queued.',
          autoDismiss: 5,
        })
        pollCount.current = 0
        lastNotifiedState.current = 'Queued'
        poll(res.call_request_id)
      })
      .catch(err => {
        // Allow a retry after a failed attempt.
        submitInFlight.current = false
        // Bail if the modal was closed/unmounted before the create rejected.
        if (!isActive.current) return
        setIsSubmitting(false)
        setErrorText(err?.body?.message || err?.message || 'Could not place the call. Please try again.')
      })
  }, [
    data,
    effectivePhone,
    effectiveLabel,
    userData,
    poll,
    notifyToast,
    setCallState,
    callView,
    agencyProfile,
  ])

  const onClose = useCallback(() => {
    // Neutralise any in-flight request + pending poll before the modal goes away.
    isActive.current = false
    stopPolling()
    closeModal()
  }, [stopPolling, closeModal])

  const header = data?.agency_name ? `Call ${data.agency_name} for fulfilment` : 'Call agency for fulfilment'

  const isTerminal = isTerminalStatus(callView?.status)

  return (
    <Modal
      header={header}
      cancelText={callView ? 'Close' : 'Cancel'}
      confirmationText={isSubmitting ? 'Placing call…' : 'Call agency'}
      isShown={isShown}
      isLoading={isSubmitting}
      canSubmit={canSubmit}
      onClose={onClose}
      onConfirm={callView ? undefined : onConfirm}
      errorText={errorText}
    >
      {!callView ? (
        <div style={{ minWidth: 480 }}>
          <p style={{ marginBottom: 8 }}>
            <strong>Who should we call?</strong>
          </p>
          {contacts.length === 0 && (
            <p style={{ color: '#a00', marginBottom: 8 }}>
              No contact number on file for this agency — type one below to place the call.
            </p>
          )}
          <div style={{ marginBottom: 12 }}>
            {contacts.map(contact => (
              <label key={contact.phone} style={{ display: 'block', marginBottom: 4 }}>
                <input
                  type="radio"
                  name="agency-call-contact"
                  value={contact.phone}
                  checked={selectedPhone === contact.phone}
                  onChange={() => setSelectedPhone(contact.phone)}
                />{' '}
                <strong>{contact.phone}</strong>
                {contact.label ? ` — ${contact.label}` : ''}
              </label>
            ))}
            <label style={{ display: 'block', marginBottom: 4 }}>
              <input
                type="radio"
                name="agency-call-contact"
                value={FREE_TYPE}
                checked={selectedPhone === FREE_TYPE}
                onChange={() => setSelectedPhone(FREE_TYPE)}
              />{' '}
              Other number…
            </label>
            {selectedPhone === FREE_TYPE && (
              <input
                type="tel"
                aria-label="Phone number"
                placeholder="+447700900123"
                value={freeTypePhone}
                onChange={e => setFreeTypePhone(e.target.value)}
                style={{ display: 'block', marginTop: 6, padding: '6px 8px', width: '100%' }}
              />
            )}
            {selectedPhone === FREE_TYPE && freeTypePhone && !isLikelyValidPhone(freeTypePhone) && (
              <p style={{ color: '#a00', marginTop: 4 }}>That does not look like a valid phone number.</p>
            )}
          </div>

          <p style={{ marginBottom: 4 }}>
            <strong>Script preview</strong>
          </p>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              background: '#f5f5f5',
              padding: 12,
              borderRadius: 4,
              fontFamily: 'inherit',
              margin: 0,
            }}
          >
            {scriptPreview}
          </pre>
          <p style={{ color: '#666', fontSize: '0.85em', marginTop: 8, marginBottom: 0 }}>
            The exact wording (including the notice band) is finalised by the call service.
          </p>
        </div>
      ) : (
        <div style={{ minWidth: 480 }}>
          <p style={{ marginBottom: 8 }}>
            Calling <strong>{effectivePhone}</strong>
            {effectiveLabel ? ` — ${effectiveLabel}` : ''}
          </p>
          <p style={{ marginBottom: 8 }}>
            Status: <strong>{describeCallState(callView) || callView?.status}</strong>
            {!isTerminal && <span style={{ color: '#666' }}> (updating live…)</span>}
          </p>
          {isTerminal && callView?.outcome && (
            <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 4 }}>
              <p style={{ margin: '0 0 4px' }}>
                <strong>Outcome:</strong> {outcomeLabel(callView.outcome.status)}
              </p>
              {callView.outcome.workers_offered != null && (
                <p style={{ margin: '0 0 4px' }}>Workers offered: {callView.outcome.workers_offered}</p>
              )}
              {callView.outcome.callback_time && (
                <p style={{ margin: '0 0 4px' }}>Callback: {callView.outcome.callback_time}</p>
              )}
              {callView.outcome.summary && <p style={{ margin: '0 0 4px' }}>{callView.outcome.summary}</p>}
              {callView.outcome.transcript_url && (
                <p style={{ margin: 0 }}>
                  <a href={callView.outcome.transcript_url} target="_blank" rel="noopener noreferrer">
                    View transcript
                  </a>
                </p>
              )}
            </div>
          )}
          {isTerminal && !callView?.outcome && (
            <p style={{ color: '#666', margin: 0 }}>
              The call has finished. The outcome and admin note will appear shortly.
            </p>
          )}
        </div>
      )}
    </Modal>
  )
}

export default storeConnector(AgencyCallModal)
